import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { updateProviderPrepTime } from 'redux/actions/providers';
import {
  isAsapSwitcherTurnOff,
  isAsapSwitcherTurnOn,
} from 'redesign/BusinessTab/BusinessPrepTime/config';
import {
  prepTimeInitialProvider,
  prepTimeSchemaProvider,
} from 'redesign/BusinessTab/BusinessPrepTime/schema/prepTimeSchema';

const usePrepTimeWebsite = (providerPrepTime) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(true);

  const prepTimeMethod = useForm({
    defaultValues: prepTimeInitialProvider,
    mode: 'all',
    resolver: yupResolver(prepTimeSchemaProvider),
  });

  const currentModel = prepTimeMethod.formState;

  const resetPrepTimeAsap = (currentModel) => {
    const findPrepTimeSchedule = isAsapSwitcherTurnOn.find(
      (item) => item?.value === Number(currentModel?.asap?.qty)
    );
    const findPrepTimeOff = isAsapSwitcherTurnOff.find(
      (item) => item?.value === Number(currentModel?.prepTime)
    );
    const disableAsap = Boolean(currentModel?.asap?.disableAsap);
    const qty =
      findPrepTimeSchedule?.value ??
      (disableAsap
        ? isAsapSwitcherTurnOn[0]?.value
        : currentModel?.asap?.qty ?? '');
    const prepTime =
      findPrepTimeOff?.value ??
      (!disableAsap
        ? isAsapSwitcherTurnOff[0]?.value
        : currentModel?.prepTime ?? '');

    return {
      orderBufferTime: {
        ...currentModel?.orderBufferTime,
      },
      asap: {
        ...currentModel.asap,
        disableAsap,
        qty,
      },
      prepTime,
    };
  };

  useEffect(() => {
    if (currentModel?.isDirty) {
      setDisabled(false);
    }
  }, [currentModel]);

  useEffect(() => {
    if (providerPrepTime?.asap && providerPrepTime?.orderBufferTime) {
      const model = resetPrepTimeAsap(providerPrepTime);
      prepTimeMethod.reset(
        {
          ...model,
        },
        { keepDirty: false }
      );
    }
  }, [providerPrepTime]);

  // Takes the value directly rather than an event, so the caller can be a
  // radio group ("Scheduled Orders Only" => ASAP disabled) instead of a switch.
  const handleChangeAsap = (disableAsap) => {
    const currentModel = prepTimeMethod.getValues();
    setDisabled(false);
    const updatedAsapSettings = {
      orderBufferTime: {
        ...(currentModel?.orderBufferTime || currentModel?.orderBufferTime),
      },
      asap: {
        ...currentModel?.asap,
        disableAsap,
      },
      prepTime: currentModel?.prepTime ?? '',
    };
    const model = resetPrepTimeAsap(updatedAsapSettings);
    prepTimeMethod.reset(
      {
        ...model,
      },
      { keepDirty: true }
    );
  };

  const handleDeliveryOrderBufferTime = (event) => {
    const currentModel = prepTimeMethod.getValues();
    setDisabled(false);
    prepTimeMethod.reset(
      {
        ...prepTimeMethod.getValues(),
        orderBufferTime: {
          ...prepTimeMethod?.getValues()?.orderBufferTime,
          deliveryOrderBufferTime: {
            startTimeMin:
              currentModel.orderBufferTime?.deliveryOrderBufferTime
                ?.startTimeMin,
            endTimeMin:
              currentModel.orderBufferTime?.deliveryOrderBufferTime?.endTimeMin,
            enabled: event.target.checked,
          },
        },
      },
      { keepDirty: true }
    );
  };

  const handlePickUpOrderBufferTime = (event) => {
    const currentModel = prepTimeMethod.getValues();
    setDisabled(false);
    prepTimeMethod.reset(
      {
        ...prepTimeMethod.getValues(),
        orderBufferTime: {
          ...prepTimeMethod?.getValues()?.orderBufferTime,
          pickUpOrderBufferTime: {
            startTimeMin:
              currentModel?.orderBufferTime?.pickUpOrderBufferTime
                ?.startTimeMin,
            endTimeMin:
              currentModel?.orderBufferTime?.pickUpOrderBufferTime?.endTimeMin,
            enabled: event.target.checked,
          },
        },
      },
      { keepDirty: true }
    );
  };

  const handleValidSubmit = () => {
    const currentValues = prepTimeMethod.getValues();
    const values = {
      ...currentValues,
      asap: {
        ...currentValues.asap,
        disableAsap: Boolean(currentValues?.asap?.disableAsap),
        qty:
          currentValues?.asap?.qty === '' || currentValues?.asap?.qty == null
            ? currentValues?.asap?.qty
            : Number(currentValues.asap.qty),
      },
      defaultPrepTime:
        currentValues?.prepTime === '' || currentValues?.prepTime == null
          ? currentValues?.prepTime
          : Number(currentValues.prepTime),
    };
    setLoading(true);
    prepTimeMethod.reset(
      {
        ...values,
        prepTime: currentValues.prepTime,
      },
      { keepDirty: false }
    );
    delete values.prepTime;
    dispatch(updateProviderPrepTime(values, 'food2'))
      .then(() => {
        setLoading(false);
        setDisabled(true);
        toast.success('Successfully updated.');
      })
      .finally(() => setLoading(false));
  };

  return {
    prepTimeMethod,
    handleValidSubmit,
    handleChangeAsap,
    handleDeliveryOrderBufferTime,
    handlePickUpOrderBufferTime,
    loading,
    disabled,
  };
};

export default usePrepTimeWebsite;
